import React from 'react';
import { FiHeart, FiUsers, FiCheckCircle, FiTrendingUp, FiTarget } from 'react-icons/fi';
import type { Campaign } from '../services/donations.service';
import donationsService from '../services/donations.service';

interface Props {
  campaign: Campaign;
  onDoar: () => void;
}

const CampanhaDestaqueCTA: React.FC<Props> = ({ campaign, onDoar }) => {
  const progress = campaign.META_VALOR > 0
    ? Math.min(100, Math.round((campaign.VALOR_ARRECADADO / campaign.META_VALOR) * 100))
    : 0;
  const restante = Math.max(0, campaign.META_VALOR - campaign.VALOR_ARRECADADO);

  return (
    <div className="relative overflow-hidden rounded-3xl shadow-xl group">
      {/* Background image */}
      <div className="absolute inset-0">
        {campaign.IMAGEM_CAPA || campaign.IMAGEM ? (
          <img
            src={campaign.IMAGEM_CAPA || campaign.IMAGEM}
            alt={campaign.TITULO}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-pink-400 via-rose-500 to-red-500" />
        )}
      </div>

      {/* Layered gradients for readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />

      {/* Accent bar top */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-500 via-rose-400 to-amber-400" />

      {/* Content */}
      <div className="relative z-10 p-6 sm:p-8 flex flex-col gap-5 min-h-[280px] sm:min-h-[320px]">

        {/* Top row: badges */}
        <div className="flex items-center gap-2 flex-wrap">
          {campaign.VERIFICADA && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/90 text-white backdrop-blur-sm">
              <FiCheckCircle className="w-3 h-3" /> Verificada
            </span>
          )}
          {campaign.DESTAQUE && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/90 text-white backdrop-blur-sm">
              ★ Destaque
            </span>
          )}
          {campaign.TRENDING && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-500/90 text-white backdrop-blur-sm">
              <FiTrendingUp className="w-3 h-3" /> Trending
            </span>
          )}
          {campaign.CATEGORIA_NOME && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-white/15 text-white/90 backdrop-blur-sm border border-white/20">
              {campaign.CATEGORIA_NOME}
            </span>
          )}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Campaign title & organizer */}
        <div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight drop-shadow-lg line-clamp-2 mb-1">
            {campaign.TITULO}
          </h2>
          {campaign.ORGANIZADOR_NOME && (
            <p className="text-white/70 text-sm">
              por <span className="text-white/90 font-medium">{campaign.ORGANIZADOR_NOME}</span>
            </p>
          )}
        </div>

        {/* Progress section */}
        <div className="space-y-2">
          {/* Bar */}
          <div className="w-full h-2.5 bg-white/20 rounded-full overflow-hidden backdrop-blur-sm">
            <div
              className="h-full rounded-full bg-gradient-to-r from-pink-400 to-amber-400 transition-all duration-700"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Stats row */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-4">
              <div>
                <p className="text-pink-300 text-xs font-medium uppercase tracking-wide">Arrecadado</p>
                <p className="text-white font-extrabold text-lg leading-none">
                  {donationsService.formatCurrency(campaign.VALOR_ARRECADADO)}
                </p>
              </div>
              <div className="w-px h-8 bg-white/20" />
              <div>
                <p className="text-white/60 text-xs font-medium uppercase tracking-wide">Meta</p>
                <p className="text-white/80 font-semibold text-base leading-none">
                  {donationsService.formatCurrency(campaign.META_VALOR)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 text-white/70 text-sm">
              <span className="flex items-center gap-1">
                <FiUsers className="w-3.5 h-3.5 text-pink-300" />
                {campaign.TOTAL_DOADORES || 0} doadores
              </span>
              <span className="flex items-center gap-1">
                <FiTarget className="w-3.5 h-3.5 text-amber-300" />
                {progress}%
              </span>
            </div>
          </div>

          {restante > 0 && (
            <p className="text-white/60 text-xs">
              Faltam <span className="text-white/90 font-semibold">{donationsService.formatCurrency(restante)}</span> para atingir a meta
            </p>
          )}
        </div>

        {/* CTA button */}
        <button
          onClick={onDoar}
          className="w-full sm:w-auto self-start flex items-center justify-center gap-2.5 px-8 py-3.5
                     bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-400 hover:to-rose-400
                     text-white font-bold text-base rounded-2xl shadow-lg shadow-pink-500/40
                     transition-all duration-200 hover:shadow-pink-500/60 hover:scale-[1.02] active:scale-[0.98]
                     cursor-pointer"
        >
          <FiHeart className="w-5 h-5" />
          Fazer Donativo
        </button>
      </div>
    </div>
  );
};

export default CampanhaDestaqueCTA;
